<template>
  <div class="page-root">
    <!-- Page Header -->
    <header class="page-header">
      <!-- <button class="back-btn" @click="router.push('/dashboard')">
        <i class="pi pi-arrow-left"></i>
        <span>Dashboard</span>
      </button> -->
       <MOPButton
              label="Dashboard"
              icon="pi pi-arrow-left"
              class="p-button-text p-button-sm"
              @click="router.push('/dashboard')"
            />
      <div class="page-title-row">
        <i class="pi pi-megaphone page-title-icon"></i>
        <h1 class="page-title">News &amp; Events</h1>
        <span class="page-count-badge">{{ filteredItems.length }} items</span>
        <button v-if="canCreate" class="create-btn" @click="router.push('/news-events/create')">
          <i class="pi pi-plus"></i> New
        </button>
      </div>
    </header>

    <!-- Filters -->
    <div class="filters-bar glass-card">
      <div class="filter-group">
        <label class="filter-label">From</label>
        <input type="date" v-model="dateFrom" class="filter-input" />
      </div>
      <div class="filter-group">
        <label class="filter-label">To</label>
        <input type="date" v-model="dateTo" class="filter-input" />
      </div>
      <div class="filter-group flex-1">
        <label class="filter-label">Search</label>
        <div class="search-wrap">
          <i class="pi pi-search search-icon"></i>
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Search news & events…"
            class="filter-input search-input"
          />
          <button v-if="searchTerm" class="clear-btn" @click="searchTerm = ''">
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>
      <button class="reset-btn" @click="resetFilters">
        <i class="pi pi-refresh"></i> Reset
      </button>
    </div>

    <!-- Category Pills -->
    <div class="cat-pills-row">
      <button
        v-for="cat in allCategories"
        :key="cat"
        class="cat-pill"
        :class="{ active: selectedCategory === cat }"
        @click="selectedCategory = cat"
      >{{ cat }}</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <i class="pi pi-spin pi-spinner text-2xl text-teal-500"></i>
      <span>Loading news &amp; events…</span>
    </div>

    <!-- Empty -->
    <div v-else-if="filteredItems.length === 0" class="empty-state glass-card">
      <i class="pi pi-inbox text-4xl text-slate-300"></i>
      <p class="text-slate-500 mt-2">No news or events found</p>
      <p class="text-slate-400 text-sm">Try adjusting the filters</p>
    </div>

    <!-- News List -->
    <div v-else class="news-list">
      <div
        v-for="item in pagedItems"
        :key="item.Id"
        class="news-card glass-card"
        style="cursor:pointer"
        @click="router.push(`/news-events/${item.Id}`)"
      >
        <div class="news-card-header">
          <div class="badges-row">
            <span class="type-badge">{{ item.EventType === 1 ? 'Event' : 'News' }}</span>
            <span v-if="item.Category" class="cat-badge">{{ item.Category }}</span>
          </div>
          <span class="news-date">{{ formatDate(item.EventDate || item.CreatedDate) }}</span>
        </div>
        <h3 class="news-title">{{ item.Title }}</h3>
        <p class="news-body">{{ item.Description }}</p>
        <div v-if="item.FullImagePath || item.AttachmentPath" class="news-footer">
          <a
            v-if="item.AttachmentPath"
            :href="item.AttachmentPath"
            target="_blank"
            class="attach-link"
          >
            <i class="pi pi-paperclip"></i> Attachment
          </a>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-row">
      <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="pi pi-chevron-left"></i>
      </button>
      <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
      <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import moment from 'moment'
import { getNewsByOrg, getDashboardUserContext } from '../../services/dashboardService'
import { useCreatePermission } from '@/composables/useCreatePermission'

const router = useRouter()
// News & Events entity — create is gated by the role's "Create" operation on it.
const NEWS_ENTITY_ID = 1026
const { canCreate, resolve: resolveCreatePermission } = useCreatePermission(NEWS_ENTITY_ID)

const loading = ref(true)
const allNews = ref<any[]>([])
const searchTerm = ref('')
const dateFrom = ref('')
const dateTo = ref('')
const selectedCategory = ref('All')
const currentPage = ref(1)
const PAGE_SIZE = 12

const allCategories = computed(() => {
  const cats = ['All', ...new Set(allNews.value.map(n => n.Category).filter(Boolean))]
  return cats
})

const filteredItems = computed(() => {
  let items = allNews.value

  if (selectedCategory.value !== 'All') {
    items = items.filter(n => n.Category === selectedCategory.value)
  }

  if (searchTerm.value.trim()) {
    const term = searchTerm.value.toLowerCase()
    items = items.filter(n =>
      (n.Title ?? '').toLowerCase().includes(term) ||
      (n.Description ?? '').toLowerCase().includes(term)
    )
  }

  if (dateFrom.value) {
    const from = moment(dateFrom.value, 'YYYY-MM-DD')
    items = items.filter(n => {
      const d = moment(n.EventDate || n.CreatedDate, ['DD-MM-YYYY', moment.ISO_8601], true)
      return d.isValid() ? d.isSameOrAfter(from, 'day') : true
    })
  }

  if (dateTo.value) {
    const to = moment(dateTo.value, 'YYYY-MM-DD')
    items = items.filter(n => {
      const d = moment(n.EventDate || n.CreatedDate, ['DD-MM-YYYY', moment.ISO_8601], true)
      return d.isValid() ? d.isSameOrBefore(to, 'day') : true
    })
  }

  return items
})

const totalPages = computed(() => Math.ceil(filteredItems.value.length / PAGE_SIZE) || 1)
const pagedItems = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE
  return filteredItems.value.slice(start, start + PAGE_SIZE)
})

function formatDate(d: any) {
  if (!d) return ''
  const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  return m.isValid() ? m.format('DD MMM YYYY') : moment(d).format('DD MMM YYYY')
}

function resetFilters() {
  searchTerm.value = ''
  dateFrom.value = ''
  dateTo.value = ''
  selectedCategory.value = 'All'
  currentPage.value = 1
}

onMounted(async () => {
  resolveCreatePermission()
  const ctx = getDashboardUserContext()
  allNews.value = await getNewsByOrg(ctx.orgId, ctx.branchId, ctx.roleId)
  loading.value = false
})
</script>

<style scoped>
.page-root {
  min-height: 100vh;
  background: #f1f5f9;
  padding: 1.5rem;
  font-family: 'Inter', sans-serif;
}

/* Header */
.page-header {
  margin-bottom: 1.25rem;
}
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 14px;
  color: #64748b;
  font-size: 0.85rem;
  cursor: pointer;
  margin-bottom: 0.75rem;
  transition: all 0.15s;
}
.back-btn:hover { background: #f8fafc; color: #0d9488; border-color: #5eead4; }
.page-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.page-title-icon { font-size: 1.4rem; color: #0d9488; }
.page-title { font-size: 1.5rem; font-weight: 700; color: #0f172a; margin: 0; }
.create-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #0d9488; color: #fff; border: none;
  border-radius: 8px; padding: 6px 14px; font-size: 0.85rem;
  cursor: pointer; margin-left: 0.5rem; transition: background 0.15s;
}
.create-btn:hover { background: #0f766e; }
.page-count-badge {
  background: #ccfbf1;
  color: #0d9488;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 0.78rem;
  font-weight: 600;
}

/* Glass Card */
.glass-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}

/* Filters */
.filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
}
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-group.flex-1 { flex: 1; min-width: 200px; }
.filter-label { font-size: 0.75rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
.filter-input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.875rem;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s;
}
.filter-input:focus { border-color: #5eead4; background: #fff; }
.search-wrap { position: relative; }
.search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 0.8rem; }
.search-input { padding-left: 30px; width: 100%; }
.clear-btn { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #94a3b8; cursor: pointer; padding: 2px; }
.clear-btn:hover { color: #64748b; }
.reset-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 16px;
  font-size: 0.85rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  align-self: flex-end;
}
.reset-btn:hover { background: #f0fdfa; color: #0d9488; border-color: #5eead4; }

/* Category pills */
.cat-pills-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1.25rem;
}
.cat-pill {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 0.8rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}
.cat-pill:hover { background: #f0fdfa; color: #0d9488; border-color: #5eead4; }
.cat-pill.active { background: #ccfbf1; border-color: #5eead4; color: #0d9488; font-weight: 600; }

/* States */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem;
  color: #64748b;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

/* News list */
.news-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.news-card {
  padding: 1.25rem;
  transition: box-shadow 0.15s, border-color 0.15s;
}
.news-card:hover { border-color: #5eead4; box-shadow: 0 2px 8px rgba(13,148,136,0.1); }
.news-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.badges-row { display: flex; gap: 6px; align-items: center; }
.type-badge {
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 600;
}
.cat-badge {
  background: #f1f5f9;
  color: #475569;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 500;
}
.news-date { font-size: 0.78rem; color: #94a3b8; }
.news-title {
  font-size: 0.98rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
}
.news-body {
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.6;
  margin: 0 0 0.75rem 0;
}
.news-footer { border-top: 1px solid #f1f5f9; padding-top: 0.75rem; }
.attach-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #0d9488;
  text-decoration: none;
  padding: 4px 10px;
  border: 1px solid #5eead4;
  border-radius: 8px;
  background: #f0fdfa;
  transition: all 0.15s;
}
.attach-link:hover { background: #ccfbf1; }

/* Pagination */
.pagination-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 0;
}
.page-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 14px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}
.page-btn:hover:not(:disabled) { background: #f0fdfa; color: #0d9488; border-color: #5eead4; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 0.85rem; color: #64748b; }
</style>
